/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import { FaPlus, FaPen, FaTrash, FaDatabase } from "react-icons/fa";

/**
 * MyButton
 * - Default variant is now "brand" (bg-brand / bg-brand/90)
 * - Text stays white
 */
export const MyButton = ({
  type = "button",
  title,
  onClick,
  children,
  disabled,
  className = "bg-black text-white border-black hover:bg-black focus-visible:ring-black",
}) => {
  // const ref = useCallback(
  //   (node) => {
  //     if (variant === "green-600" && node) {
  //       node.classList.replace("bg-green-800", "bg-green-600");
  //     }
  //   },
  //   [variant]
  // );

  // Palette by variant
  // const variantPalette = {
  //   "green-600": "bg-green-600 hover:bg-green-700",
  //   brand: "bg-brand hover:bg-brand/90",
  //   red: "bg-red-600 hover:bg-red-700",
  //   blue: "bg-blue-600 hover:bg-blue-700",
  //   purple: "bg-purple-600 hover:bg-purple-700",
  //   black: "bg-black hover:bg-black/90",
  //   default: "bg-brand hover:bg-brand/90", 
  // };


  const base =
    "relative group inline-flex items-center justify-center " +
    "min-w-fit w-fit rounded-xl px-6 py-3 text-sm font-semibold text-white " +
    "transition duration-200 ease-out " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 " +
    "shadow-sm hover:shadow-md active:shadow disabled:opacity-60 disabled:pointer-events-none overflow-hidden";
  const glow =
    "before:absolute before:inset-0 before:bg-white/0 before:transition before:duration-200 group-hover:before:bg-white/[0.06]";
  const sheen =
    "after:absolute after:inset-y-0 after:-left-1/3 after:w-1/3 after:bg-white/20 after:skew-x-12 " +
    "after:opacity-0 group-hover:after:opacity-100 group-hover:after:translate-x-[260%] " +
    "after:transition-transform after:duration-700";

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${base} ${glow} ${sheen} ${className}`}
      aria-disabled={disabled ? "true" : "false"}
      data-variant={"brand"}
    >
      {title || children}
    </button>
  );
};

/**
 * Button
 * - Same `props` API (props.type, props.title, props.category, props.action, props.onClick)
 * - Category colors mapped to polished tones; keeps your add/edit/delete/level icons
 * - Adds premium surface, clean spacing, a11y, and hover sheen
 */
export const Button = ({ props }) => {
  const category = props?.category ? props?.category : "default";

  // Enumerate Tailwind classes so JIT can see them.
  const palette = {
    default: ["bg-brand", "hover:bg-brand/90"],
    red: ["bg-red-700/80", "hover:bg-red-800"],
    blue: ["bg-blue-700", "hover:bg-blue-800"],
    green: ["bg-green-700", "hover:bg-green-800"],
    orange: ["bg-orange-600", "hover:bg-orange-700"],
    black: ["bg-black/90", "hover:bg-black"],
    purple: ["bg-purple-700", "hover:bg-purple-800"],
  };

  // If default / add / edit -> treat as default brand color
  const resolved =
    category === "default" || props?.action === "add" || props?.action === "edit"
      ? "default"
      : category;

  const [bgClass, hoverClass] = palette[resolved] || palette.default;

  const base =
    "relative group inline-flex items-center justify-center " +
    "rounded-xl px-6 py-2 text-xs lg:text-sm font-semibold text-white capitalize " +
    "transition duration-200 ease-out " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 " +
    "shadow-sm hover:shadow-md active:shadow overflow-hidden";
  const glow =
    "before:absolute before:inset-0 before:bg-white/0 before:transition before:duration-200 group-hover:before:bg-white/[0.06]";
  const sheen =
    "after:absolute after:inset-y-0 after:-left-1/3 after:w-1/3 after:bg-white/20 after:skew-x-12 " +
    "after:opacity-0 group-hover:after:opacity-100 group-hover:after:translate-x-[260%] " +
    "after:transition-transform after:duration-700";

  const Icon =
    props?.action?.toString().toLowerCase() === "edit"
      ? FaPen
      : props?.action?.toString().toLowerCase() === "delete"
        ? FaTrash
        : props?.action?.toString().toLowerCase() === "add"
          ? FaPlus
          : props?.action?.toString().toLowerCase() === "level"
            ? FaDatabase
            : null;

  return (
    <button
      type={props.type}
      onClick={props.onClick}
      className={`${base} ${bgClass} ${hoverClass} ${glow} ${sheen}`}
      aria-label={props.title}
      data-category={resolved}
      data-action={props?.action || ""}
    >
      {Icon ? <Icon className="mr-2 h-3.5 w-3.5" aria-hidden="true" /> : <span className="hidden" />}
      <span className="whitespace-nowrap">{props.title}</span>
    </button>
  );
};

/**
 * DisabledButton
 * - Same `props` API (props.color, props.title, props.action)
 * - Non-interactive, consistent styling with the rest, clear disabled affordance
 */
export const DisabledButton = ({ props }) => {
  const base =
    "inline-flex items-center justify-center rounded-xl px-6 py-2 text-xs lg:text-sm font-semibold capitalize " +
    "cursor-not-allowed text-white opacity-70 select-none " +
    "shadow-sm bg-gray-400"; // neutral fallback if props.color is missing

  // If a color is provided, layer it behind the neutral to keep your theme
  const colorLayer = props.color ? `${props.color}` : "";

  return (
    <button
      type="button"
      aria-disabled="true"
      disabled
      className={`${base} ${colorLayer}`}
    >
      {props.action === "delete" ? (
        <FaTrash className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
      ) : (
        <span className="hidden" />
      )}
      <span className="whitespace-nowrap">{props.title}</span>
    </button>
  );
};
